/**
 * Private platform Auth v2 surface (dashboard monorepo only).
 * Full lifecycle: sign-in / sign-up / 6-digit OTP / forgot / reset.
 * Calls first-party `/v1/platform/auth/*` — not Hosted portal transaction login.
 */
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  digitsOnlyOtp,
  platformForgotPassword,
  platformResendEmailOtp,
  platformResetPassword,
  platformSignIn,
  platformSignUp,
  platformVerifyEmailOtp,
  type PlatformAuthView,
  type PlatformOtpPending,
} from "@/lib/platformAuth";
import { type Session } from "@/lib/api";
import { ROUTES } from "@/routes";

export type PlatformAuthSurfaceProps = {
  /** Initial view when the surface mounts. */
  defaultView?: PlatformAuthView;
  /** Called after a usable platform session is established. */
  onAuthenticated: (session: Session & { access_token: string; refresh_token: string }) => void;
  /** Optional class on the outer shell. */
  className?: string;
  /** Show brand header (full-page login). */
  showBrand?: boolean;
};

const TITLES: Record<PlatformAuthView, [string, string]> = {
  "sign-in": ["Welcome back", "Sign in to the TinyBase console."],
  "sign-up": [
    "Create your account",
    "A 6-digit code will be sent to verify your email.",
  ],
  otp: ["Check your email", "Enter the 6-digit code we sent you."],
  forgot: [
    "Forgot password",
    "We'll email a 6-digit code if the account is eligible.",
  ],
  reset: ["Reset password", "Enter the code and choose a new password."],
};

export function PlatformAuthSurface({
  defaultView = "sign-in",
  onAuthenticated,
  className,
  showBrand = true,
}: PlatformAuthSurfaceProps) {
  const [view, setView] = useState<PlatformAuthView>(defaultView);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [pending, setPending] = useState<PlatformOtpPending | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const titleId = useId();
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    setView(defaultView);
    setError("");
    setSuccess("");
  }, [defaultView]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const clearNotice = () => {
    setError("");
    setSuccess("");
  };

  const go = (next: PlatformAuthView) => {
    clearNotice();
    setView(next);
  };

  const otpCode = otp.join("");

  const setOtpAt = (index: number, raw: string) => {
    const d = digitsOnlyOtp(raw);
    if (d.length > 1) {
      const chars = d.split("");
      const next = ["", "", "", "", "", ""];
      for (let i = 0; i < 6; i++) next[i] = chars[i] ?? "";
      setOtp(next);
      otpRefs.current[Math.min(chars.length, 5)]?.focus();
      return;
    }
    const next = [...otp];
    next[index] = d;
    setOtp(next);
    if (d && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const onOtpKey = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const applyStep = async (
    run: () => Promise<
      Awaited<ReturnType<typeof platformSignIn>>
    >,
  ) => {
    clearNotice();
    setBusy(true);
    try {
      const step = await run();
      if (step.kind === "session") {
        onAuthenticated(step.session);
        return;
      }
      if (step.kind === "otp") {
        setPending(step.pending);
        setOtp(["", "", "", "", "", ""]);
        setResendIn(30);
        go("otp");
        return;
      }
      if (step.kind === "reset") {
        setPending(step.pending);
        setOtp(["", "", "", "", "", ""]);
        setResendIn(30);
        if (step.message) setSuccess(step.message);
        go("reset");
        return;
      }
      if (step.kind === "message") {
        if (view === "reset") {
          setPassword("");
          setNewPassword("");
          setConfirmPassword("");
          setOtp(["", "", "", "", "", ""]);
          setView("sign-in");
        }
        setError("");
        setSuccess(step.message);
        return;
      }
      setError(step.message);
    } finally {
      setBusy(false);
    }
  };

  const onSignIn = (event: FormEvent) => {
    event.preventDefault();
    void applyStep(() => platformSignIn(email, password));
  };

  const onSignUp = (event: FormEvent) => {
    event.preventDefault();
    void applyStep(() => platformSignUp(email, password, username));
  };

  const onVerifyOtp = (event: FormEvent) => {
    event.preventDefault();
    void applyStep(() =>
      platformVerifyEmailOtp({
        code: otpCode,
        email: pending?.email || email,
        userId: pending?.userId,
      }),
    );
  };

  const onResendOtp = () => {
    if (resendIn > 0 || busy) return;
    void applyStep(async () => {
      const step = await platformResendEmailOtp({
        email: pending?.email || email,
        userId: pending?.userId,
      });
      if (step.kind === "message") {
        setResendIn(30);
      }
      return step;
    });
  };

  const onForgot = (event: FormEvent) => {
    event.preventDefault();
    void applyStep(() => platformForgotPassword(email));
  };

  const onReset = (event: FormEvent) => {
    event.preventDefault();
    void applyStep(() =>
      platformResetPassword({
        email: pending?.email || email.trim(),
        code: otpCode,
        newPassword,
        confirmPassword,
      }),
    );
  };

  const [title, subtitleBase] = TITLES[view];
  const subtitle =
    view === "otp"
      ? `Enter the 6-digit code sent to ${pending?.email || "your email"}.`
      : subtitleBase;

  const otpFields = (
    <div
      className="flex justify-center gap-2"
      role="group"
      aria-label="6-digit verification code"
    >
      {otp.map((digit, i) => (
        <Input
          key={i}
          ref={(el) => {
            otpRefs.current[i] = el;
          }}
          className="h-12 w-10 px-0 text-center font-mono text-lg tracking-widest sm:w-11"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={6}
          aria-label={`Digit ${i + 1}`}
          value={digit}
          onChange={(e) => setOtpAt(i, e.target.value)}
          onKeyDown={(e) => onOtpKey(i, e)}
          onPaste={(e) => {
            e.preventDefault();
            setOtpAt(i, e.clipboardData.getData("text"));
          }}
        />
      ))}
    </div>
  );

  return (
    <div
      className={cn(
        "auth-surface page-enter w-full max-w-md rounded-xl border border-border bg-surface-container-lowest p-6 shadow-modal sm:p-8",
        className,
      )}
      role="region"
      aria-labelledby={titleId}
      data-testid="platform-auth-surface"
      data-auth-view={view}
    >
      {showBrand ? (
        <div className="mb-6 flex items-center gap-3">
          <BrandMark />
          <div>
            <div className="auth-brand-name font-display text-sm font-semibold tracking-tight text-text-primary">
              TinyBase
            </div>
            <div className="font-mono text-[10px] uppercase tracking-label text-text-muted">
              console
            </div>
          </div>
        </div>
      ) : null}

      <h1
        id={titleId}
        className="font-display text-xl font-semibold tracking-tight text-text-primary"
      >
        {title}
      </h1>
      <p className="mt-1.5 text-sm text-text-secondary">{subtitle}</p>

      {error ? (
        <div
          className="mt-4 rounded-md border border-error/30 bg-error-container px-3 py-2 text-sm text-on-error-container"
          role="alert"
          data-testid="platform-auth-error"
        >
          {error}
        </div>
      ) : null}
      {success ? (
        <div
          className="mt-4 rounded-md border border-success/30 bg-success-container px-3 py-2 text-sm text-on-success-container"
          role="status"
          data-testid="platform-auth-success"
        >
          {success}
        </div>
      ) : null}

      {view === "sign-in" ? (
        <form className="mt-6 space-y-4" onSubmit={onSignIn} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="platform-auth-email">Email</Label>
            <Input
              id="platform-auth-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="platform-auth-password">Password</Label>
            <Input
              id="platform-auth-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              className="text-sm font-medium text-secondary hover:underline"
              onClick={() => go("forgot")}
            >
              Forgot password?
            </button>
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-center text-sm text-text-secondary">
            New here?{" "}
            <button
              type="button"
              className="font-medium text-secondary hover:underline"
              onClick={() => go("sign-up")}
            >
              Create an account
            </button>
          </p>
        </form>
      ) : null}

      {view === "sign-up" ? (
        <form className="mt-6 space-y-4" onSubmit={onSignUp} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="platform-auth-signup-email">Email</Label>
            <Input
              id="platform-auth-signup-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="platform-auth-signup-username">
              Username (optional)
            </Label>
            <Input
              id="platform-auth-signup-username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="platform-auth-signup-password">Password</Label>
            <Input
              id="platform-auth-signup-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Creating account…" : "Create account"}
          </Button>
          <p className="text-center text-sm text-text-secondary">
            Already have an account?{" "}
            <button
              type="button"
              className="font-medium text-secondary hover:underline"
              onClick={() => go("sign-in")}
            >
              Sign in
            </button>
          </p>
        </form>
      ) : null}

      {view === "otp" ? (
        <form className="mt-6 space-y-4" onSubmit={onVerifyOtp} noValidate>
          {otpFields}
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Verifying…" : "Verify email"}
          </Button>
          <div className="flex flex-col items-center gap-2 text-sm">
            <button
              type="button"
              className="font-medium text-secondary hover:underline disabled:opacity-40"
              disabled={busy || resendIn > 0}
              onClick={onResendOtp}
            >
              {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
            </button>
            <button
              type="button"
              className="text-text-secondary hover:underline"
              onClick={() => go("sign-in")}
            >
              Back to sign in
            </button>
          </div>
        </form>
      ) : null}

      {view === "forgot" ? (
        <form className="mt-6 space-y-4" onSubmit={onForgot} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="platform-auth-forgot-email">Email</Label>
            <Input
              id="platform-auth-forgot-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Sending…" : "Send reset code"}
          </Button>
          <p className="text-center text-sm">
            <button
              type="button"
              className="font-medium text-secondary hover:underline"
              onClick={() => go("sign-in")}
            >
              Back to sign in
            </button>
          </p>
        </form>
      ) : null}

      {view === "reset" ? (
        <form className="mt-6 space-y-4" onSubmit={onReset} noValidate>
          {otpFields}
          <div className="space-y-1.5">
            <Label htmlFor="platform-auth-new-password">New password</Label>
            <Input
              id="platform-auth-new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="platform-auth-confirm-password">
              Confirm password
            </Label>
            <Input
              id="platform-auth-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Updating…" : "Update password"}
          </Button>
          <p className="text-center text-sm">
            <button
              type="button"
              className="font-medium text-secondary hover:underline"
              onClick={() => go("sign-in")}
            >
              Back to sign in
            </button>
          </p>
        </form>
      ) : null}

      <p className="mt-6 text-center text-xs text-text-muted">
        <Link to={ROUTES.home} className="hover:text-text-secondary">
          Back to home
        </Link>
        {" · "}
        <Link to={ROUTES.docs} className="hover:text-text-secondary">
          Docs
        </Link>
      </p>
    </div>
  );
}
